<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__name><div class="table-default__left">Name</div></template>
        <template #name="{ row }"><div class="table-default__left">{{ row.name }}</div></template>
        <template #tour_start="{ row }">{{ formatDate(row.tour_start) }}</template>
        <template #tour_end="{ row }">{{ formatDate(row.tour_end) }}</template>
        <template #duration="{ row }">{{ row.duration }}</template>
        <template #permits_amount="{ row }">{{ formatAmount(row.price_info.permits_amount) }}</template>
        <template #crew_salary_amount="{ row }">{{ formatAmount(row.price_info.crew_salary_amount) }}</template>
        <template #clients_revenue_amount="{ row }">{{ formatAmount(row.price_info.clients_revenue_amount) }}</template>
        <template #total_amount="{ row }">{{ formatAmount(row.price_info.total_amount) }}</template>
        <template #amount_paid="{ row }">{{ formatAmount(row.price_info.amount_paid) }}</template>
        <template #balance="{ row }">{{ formatAmount(row.price_info.balance) }}</template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowModal(row)" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
    <modal
      name="edit_permits"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('edit_permits')"></button>
        <div class="popup__title">Edit permits</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Permits amount" label-for="permit_amount" required>
              <app-fin-number
                v-model="form.permits_amount"
                id="permit_amount"
                name="permit_amount"
                :error="$v.form.permits_amount.$error"
              />
              <template #error>
                <div v-if="$v.form.permits_amount.$dirty && !$v.form.permits_amount.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Crew salary amount" label-for="crew_salary_amount" required>
              <app-fin-number
                v-model="form.crew_salary_amount"
                id="crew_salary_amount"
                name="crew_salary_amount"
                :error="$v.form.crew_salary_amount.$error"
              />
              <template #error>
                <div v-if="$v.form.crew_salary_amount.$dirty && !$v.form.crew_salary_amount.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button ref="submit_permits" :disabled="$v.form.$error" type="submit">Save</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { getClimbingGroupsReimbursement, patchClimbingGroupsReimbursement } from '@/http'
import { format, parseISO } from 'date-fns'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'AlturaReimbursementGroups',
  data() {
    return {
      page_loaded: false,
      table: {
        items: [],
        columns: [
          'name',
          'tour_start',
          'tour_end',
          'duration',
          'permits_amount',
          'crew_salary_amount',
          'clients_revenue_amount',
          'total_amount',
          'amount_paid',
          'balance',
          'controls',
        ],
        options: {
          headings: {
            'name': 'Name',
            'tour_start': 'Tour start',
            'tour_end': 'Tour end',
            'duration': 'Duration',
            'permits_amount': 'Permits amount',
            'crew_salary_amount': 'Crew salary amount',
            'clients_revenue_amount': 'Clients revenue',
            'total_amount': 'Total amount',
            'amount_paid': 'Amount paid',
            'balance': 'Balance',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
      form: {},
      item_to_action: null,
    }
  },
  validations: {
    form: {
      permits_amount: { required },
      crew_salary_amount: { required },
    }
  },
  created() {
    this.fetchItems()
  },
  methods: {
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly'
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit_permits.preload = true
      patchClimbingGroupsReimbursement(this.item_to_action, this.form)
        .then(() => {
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Changes saved'
          })
          this.$modal.hide('edit_permits')
        })
        .catch(() => { this.$refs.submit_permits.preload = false })
    },
    onShowModal(item) {
      this.item_to_action = item.id
      this.form = {
        permits_amount: item.price_info.permits_amount,
        crew_salary_amount: item.price_info.crew_salary_amount,
      }
      this.$v.form.$reset()
      this.$modal.show('edit_permits')
    },
    async fetchItems() {
      this.table_preloader = true
      const response = await getClimbingGroupsReimbursement({
        page: this.pagination.page,
        page_size: 40,
      })
      this.table.items = response.data.results
      this.pagination.pages = response.data.pages
      this.pagination.count = response.data.count
      this.table_preloader = false
      this.page_loaded = true
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    formatDate(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    formatAmount(string) {
      return string ? new Intl.NumberFormat('ru-RU').format(string) : '-'
    },
  }
}
</script>
